/**
 * src/pages/admin/AdminDashboard.js
 * 
 * Central admin hub — the main dashboard for all admin operations.
 * Displays platform stats, quick actions, recent activity, and health cards.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ShoppingBag,
  TicketCheck,
  Bell,
  MessageSquareText,
  Activity,
  TrendingUp,
  FileWarning,
  Shield,
  ArrowUpRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  UserPlus,
  ScrollText,
  Megaphone,
} from "lucide-react";
import { useAdmin } from "../../hooks/useAdmin";
import * as adminService from "../../services/adminService";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import SystemHealthPanel from "../../components/admin/SystemHealthPanel";
import { errorDev } from '../../utils/logger.js';

// ─── Stat Card ──────────────────────────────────────────────────
function StatCard({ title, value, Icon, color, trend, to }) {
  const colorMap = {
    green: {
      bg: "bg-green-100 dark:bg-green-900/30",
      icon: "text-green-600 dark:text-green-400",
      border: "border-green-200 dark:border-green-800",
    },
    blue: {
      bg: "bg-blue-100 dark:bg-blue-900/30",
      icon: "text-blue-600 dark:text-blue-400",
      border: "border-blue-200 dark:border-blue-800",
    },
    amber: {
      bg: "bg-amber-100 dark:bg-amber-900/30",
      icon: "text-amber-600 dark:text-amber-400",
      border: "border-amber-200 dark:border-amber-800",
    },
    purple: {
      bg: "bg-purple-100 dark:bg-purple-900/30",
      icon: "text-purple-600 dark:text-purple-400",
      border: "border-purple-200 dark:border-purple-800",
    },
    rose: {
      bg: "bg-rose-100 dark:bg-rose-900/30",
      icon: "text-rose-600 dark:text-rose-400",
      border: "border-rose-200 dark:border-rose-800",
    },
    cyan: {
      bg: "bg-cyan-100 dark:bg-cyan-900/30",
      icon: "text-cyan-600 dark:text-cyan-400",
      border: "border-cyan-200 dark:border-cyan-800",
    },
  };
  const c = colorMap[color] || colorMap.green;

  const content = (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl border ${c.border} p-4 hover:shadow-md transition group`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
        {to && (
          <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition" />
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {value != null ? value.toLocaleString() : "—"}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{title}</p>
      </div>
      {trend != null && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
          <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          {trend >= 0 ? '+' : ''}{trend} this week
        </div>
      )}
    </div>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }
  return content;
}

// ─── Quick Action Card ──────────────────────────────────────────
function QuickAction({ to, Icon, label, description }) {
  return (
    <Link
      to={to}
      className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4 hover:border-green-500 dark:hover:border-green-600 transition group"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex-shrink-0 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">{label}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{description}</p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 flex-shrink-0" />
      </div>
    </Link>
  );
}

// ─── Activity Item ──────────────────────────────────────────────
function ActivityItem({ log }) {
  const actionMap = {
    SEND_NOTIFICATION: { Icon: Bell, color: "text-blue-500" },
    UPDATE_USER_ROLE: { Icon: Shield, color: "text-purple-500" },
    BAN_USER: { Icon: AlertTriangle, color: "text-red-500" },
    UNBAN_USER: { Icon: CheckCircle2, color: "text-green-500" },
    UPDATE_TICKET: { Icon: TicketCheck, color: "text-amber-500" },
    REPLY_TICKET: { Icon: MessageSquareText, color: "text-cyan-500" },
    MODERATE_PRODUCT: { Icon: FileWarning, color: "text-rose-500" },   
    UPDATE_FEEDBACK_STATUS: { Icon: MessageSquareText, color: "text-indigo-500" },
    DEACTIVATE_NOTIFICATION: { Icon: Bell, color: "text-gray-500" },
  };

  const config = actionMap[log.action] || { Icon: Activity, color: "text-gray-500" };
  const { Icon } = config;

  const timeAgo = log.timestamp?.seconds
    ? formatTimeAgo(new Date(log.timestamp.seconds * 1000))
    : "";

  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className={`w-8 h-8 rounded-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">{log.humanDescription || log.description || log.action?.replace(/_/g, ' ')}</span>
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {log.adminEmail} · {timeAgo}
        </p>
      </div>
    </div>
  );
}

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

// ─── Main Dashboard ─────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, isSuperAdmin } = useAdmin();
  const [stats, setStats] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [signupData, setSignupData] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const data = await adminService.getPlatformStats();
      setStats(data);
    } catch (err) {
      // Silent fail
    errorDev("Operation failed:", err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadSignupChart = useCallback(async () => {
    try {
      const users = await adminService.getRecentSignups(30);
      // Group by day
      const byDay = {};
      users.forEach((u) => {
        const ts = u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000) : new Date(u.createdAt || 0);
        const day = ts.toISOString().slice(0, 10);
        byDay[day] = (byDay[day] || 0) + 1;
      });
      // Fill gaps
      const chartData = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        chartData.push({
          date: key,
          label: d.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
          signups: byDay[key] || 0,
        });
      }
      setSignupData(chartData);
    } catch (err) {
      // Silent fail
    errorDev("Operation failed:", err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadSignupChart();
  }, [loadStats, loadSignupChart]);

  useEffect(() => {
    const unsub = adminService.listenActivityLog((logs) => {
      setActivityLog(logs);
    }, 15);
    return () => unsub();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStats();
    await loadSignupChart();
    setRefreshing(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 lg:border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                Welcome back, {user?.email?.split("@")[0] || "Admin"}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition disabled:opacity-50"
            >
              {refreshing ? (
                <span className="orrith-loader orrith-loader-xs orrith-loader-primary" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {/* Stats Grid */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers}
            Icon={Users}
            color="green"
            trend={stats?.newUsersThisWeek}
            to="/admin/users"
          />
          <StatCard
            title="Products"
            value={stats?.totalProducts}
            Icon={ShoppingBag}
            color="blue"
            to="/admin/moderation"
          />
          <StatCard
            title="Support Tickets"
            value={stats?.totalTickets}
            Icon={TicketCheck}
            color="amber"
            to="/admin/tickets"
          />
          <StatCard
            title="Open Tickets"
            value={stats?.openTickets}
            Icon={Clock}
            color="rose"
            to="/admin/tickets"
          />
          <StatCard
            title="Ratings"
            value={stats?.totalRatings}
            Icon={TrendingUp}
            color="cyan"
          />
          <StatCard
            title="Open Reports"
            value={stats?.openReports}
            Icon={AlertTriangle}
            color="rose"
            to="/admin/reports"
          />
        </section>

        {/* Two Column Layout: Chart + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Signup Chart */}
          <div className="lg:col-span-2 bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                  User Growth
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  New signups last 30 days
                </p>
              </div>
              <UserPlus className="w-5 h-5 text-gray-400" />
            </div>

            <div className="h-48">
              {signupData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={signupData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(v) => [`${v} signups`, '']}
                      labelFormatter={(l) => l}
                    />
                    <Area
                      type="monotone"
                      dataKey="signups"
                      stroke="#16a34a"
                      fill="url(#signupGrad)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                  Loading chart data…
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                Recent Activity
              </h2>
              <Link
                to="/admin/activity"
                className="text-xs text-green-600 dark:text-green-400 hover:underline"
              >
                View all
              </Link>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-neutral-700 max-h-52 overflow-y-auto">
              {activityLog.length > 0 ? (
                activityLog.slice(0, 8).map((log) => (
                  <ActivityItem key={log.id} log={log} />
                ))
              ) : (
                <p className="text-sm text-gray-400 py-4 text-center">No activity yet</p>
              )}
            </div>
          </div>
        </div>

        {/* System Health Panel */}
        <SystemHealthPanel />

        {/* Quick Actions */}
        <section>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <QuickAction
              to="/admin/notifications"
              Icon={Bell}
              label="Send Notification"
              description="Broadcast announcements or send targeted messages"
            />
            <QuickAction
              to="/admin/users"
              Icon={Users}
              label="Manage Users"
              description="View, search, assign roles, ban/unban users"
            />
            <QuickAction
              to="/admin/tickets"
              Icon={TicketCheck}
              label="Support Tickets"
              description="Review and respond to user support requests"
            />
            <QuickAction
              to="/admin/feedback"
              Icon={MessageSquareText}
              label="View Feedback"
              description="Read user feedback and mark reviewed"
            />
            <QuickAction
              to="/admin/moderation"
              Icon={FileWarning}
              label="Content Moderation"
               description="Review flagged products"
            />
            <QuickAction
              to="/admin/reports"
              Icon={AlertTriangle}
              label="Review Reports"
               description="Process profile and product reports"
            />
            <QuickAction
              to="/admin/analytics"
              Icon={Activity}
              label="System Health"
              description="Platform analytics and performance metrics"
            />
            <QuickAction
              to="/admin/popups"
              Icon={Megaphone}
              label="Popup Manager"
              description="Send targeted or broadcast popups to users"
            />
          </div>
        </section>
      </div>
    </div>
  );
}
